import React, { useState, useEffect } from "react";
import { Calendar } from "lucide-react";
import ReusableDropdown from "../../../components/ReusableDropdown";
import { apiFetch } from "../../../context/AuthContext";
import { normalizeInfinityCodeValue } from "../hooks/useLeaveData";
import { getLeaveTypeColor, getLeaveTypeIcon, getTwoInitials } from "../utils/leaveHelpers";

const apiBaseURL = process.env.REACT_APP_BASE_URL;

const TeamLeaveCalendar = ({
  managerInfinityCode,
  employeeImages,
  isDarkMode,
  screenSize,
  fonts,
  spacing,
  theme,
  language,
  t,
}) => {
  const normalizedManagerCode = normalizeInfinityCodeValue(managerInfinityCode);
  const [teamLeaves, setTeamLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    const fetchTeamLeaves = async () => {
      setLoading(false);
      try {
        const encodedManagerCode = encodeURIComponent(normalizedManagerCode);
        const url = `${apiBaseURL}/calendar/team/leaves/${encodedManagerCode}?year=${encodeURIComponent(selectedYear)}&month=${encodeURIComponent(selectedMonth)}&status=approved`;
        const res = await apiFetch(url, { method: "GET" });
        if (!res.ok) throw new Error(`Error ${res.status}`);
        const data = await res.json();
        const leaves =
          data?.data?.leaves ||
          data?.data?.data?.leaves ||
          data?.data ||
          data?.leaves ||
          [];
        console.log("📊 Team Leaves Raw Data:", leaves);
        setTeamLeaves(Array.isArray(leaves) ? leaves : []);
      } catch (error) {
        console.error("Error fetching team leaves:", error);
        setTeamLeaves([]);
      } finally {
        setLoading(false);
      }
    };

    if (normalizedManagerCode === null) {
      setTeamLeaves([]);
      setLoading(false);
      return;
    }

    fetchTeamLeaves();
  }, [normalizedManagerCode, selectedMonth, selectedYear]);

  const cardStyle = {
    background: isDarkMode
      ? "rgba(255, 255, 255, 0.01)"
      : "rgba(255, 255, 255, 0.9)",
    backdropFilter: "blur(1.5625rem)",
    WebkitBackdropFilter: "blur(1.5625rem)",
    border: isDarkMode
      ? "1px solid rgba(255, 255, 255, 0.1)"
      : "1px solid rgba(0, 0, 0, 0.05)",
    borderRadius: screenSize === "xs" ? "1rem" : "1.25rem",
    padding: spacing.padding,
    boxShadow: isDarkMode
      ? "0 0.5rem 2rem rgba(0, 0, 0, 0.2)"
      : "0 0.5rem 2rem rgba(0, 0, 0, 0.1)",
    marginBottom: spacing.margin,
    transition: "all 0.3s ease",
  };

  if (loading) {
    return <div style={cardStyle}>{t.team.loading}</div>;
  }

  return (
    <div style={cardStyle}>
      <h3
        style={{
          color: isDarkMode ? "white" : "#1a1a1a",
          fontSize: fonts.subtitle,
          fontWeight: "600",
          margin: `0 0 ${spacing.margin} 0`,
          display: "flex",
          alignItems: "center",
          gap: screenSize === "xs" ? "0.5rem" : "0.5rem",
          fontFamily: "'Cairo', sans-serif",
        }}
      >
        <Calendar size={24} color="#2e7cba" />
        {t.team.title}
      </h3>

      {/* Month & Year selectors */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: screenSize === "xs" ? "1fr" : "1fr 1fr",
          gap: "16px",
          marginBottom: spacing.margin,
          padding: spacing.padding,
        }}
      >
        <div>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: fonts.small,
              fontWeight: "600",
              color: isDarkMode
                ? "rgba(255, 255, 255, 0.7)"
                : "rgba(26, 26, 26, 0.7)",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            {language === "ar" ? "الشهر" : "Month"}
          </label>
          <ReusableDropdown
            items={Object.entries(t.months).map(([value, label]) => ({
              id: value,
              name: label,
            }))}
            value={t.months[selectedMonth] || ""}
            onChange={(val) => {
              const monthEntry = Object.entries(t.months).find(
                ([, label]) =>
                  String(label).toLowerCase() === String(val).toLowerCase()
              );
              if (monthEntry) setSelectedMonth(parseInt(monthEntry[0]));
            }}
            onSelect={(item) => setSelectedMonth(parseInt(item.id))}
            placeholder={language === "ar" ? "اختر الشهر" : "Select Month"}
            getItemKey={(item) => item.id}
            getItemLabel={(item) => item.name}
            theme={isDarkMode ? "dark" : "light"}
            language={language}
            maxItems={12}
            showAllOnArrowClick={true}
            showClear={false}
            inputStyle={{ fontWeight: "600", fontSize: fonts.body }}
          />
        </div>

        <div>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: fonts.small,
              fontWeight: "600",
              color: isDarkMode
                ? "rgba(255, 255, 255, 0.7)"
                : "rgba(26, 26, 26, 0.7)",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            {language === "ar" ? "السنة" : "Year"}
          </label>
          <ReusableDropdown
            items={[2024, 2025, 2026].map((year) => ({
              id: year.toString(),
              name: year.toString(),
            }))}
            value={selectedYear.toString()}
            onChange={(val) => {
              const yearNum = parseInt(val);
              if (!isNaN(yearNum) && [2024, 2025, 2026].includes(yearNum)) {
                setSelectedYear(yearNum);
              }
            }}
            onSelect={(item) => setSelectedYear(parseInt(item.id))}
            placeholder={language === "ar" ? "اختر السنة" : "Select Year"}
            getItemKey={(item) => item.id}
            getItemLabel={(item) => item.name}
            theme={isDarkMode ? "dark" : "light"}
            language={language}
            maxItems={10}
            showAllOnArrowClick={true}
            showClear={false}
            inputStyle={{ fontWeight: "600", fontSize: fonts.body }}
          />
        </div>
      </div>

      {/* Team Leaves List */}
      <div style={{ maxHeight: "600px", overflowY: "auto" }}>
        {teamLeaves.length === 0 ? (
          <div
            style={{
              padding: "40px 20px",
              textAlign: "center",
              color: isDarkMode
                ? "rgba(255, 255, 255, 0.6)"
                : "rgba(26, 26, 26, 0.6)",
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            <Calendar size={48} style={{ opacity: 0.3, marginBottom: "16px" }} />
            <p>{t.team.noLeaves}</p>
          </div>
        ) : (
          teamLeaves.map((leave, index) => {
            const LeaveIcon = getLeaveTypeIcon(leave.leaveType);
            return (
              <div
                key={leave.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "20px",
                  padding: "20px 0",
                  borderBottom:
                    index < teamLeaves.length - 1
                      ? `1px solid ${
                          isDarkMode
                            ? "rgba(255, 255, 255, 0.1)"
                            : "rgba(0, 0, 0, 0.1)"
                        }`
                      : "none",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = isDarkMode
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0, 0, 0, 0.02)";
                  e.currentTarget.style.borderRadius = "12px";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                {/* Employee Avatar */}
                <div
                  data-infinity-code={leave.infinityCode}
                    style={{
                    width: "64px",
                    height: "64px",
                      background: `linear-gradient(135deg, #2e7cba, #2e7cba)`,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#ffffff",
                    fontWeight: "600",
                    fontSize: "1.25rem",
                    flexShrink: 0,
                    fontFamily: "'Cairo', sans-serif",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {employeeImages[leave.infinityCode] && (
                    <img
                      src={employeeImages[leave.infinityCode]}
                      alt={leave.employeeName}
                      style={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                        objectFit: "cover",
                        position: "absolute",
                        inset: 0,
                        zIndex: 2,
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  )}
                  <div
                    style={{
                      position: employeeImages[leave.infinityCode]
                        ? "absolute"
                        : "relative",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 1,
                    }}
                  >
                    {getTwoInitials(
                      language === "ar"
                        ? leave.arabicName || leave.employeeName
                        : leave.sr || leave.employeeName
                    )}
                  </div>
                </div>

                {/* Leave Details */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h4
                    style={{
                      color: isDarkMode ? "white" : "#1a1a1a",
                      fontSize: "1.125rem",
                      fontWeight: "600",
                      margin: "0 0 6px 0",
                      fontFamily: "'Cairo', sans-serif",
                    }}
                  >
                    {language === "ar"
                      ? leave.arabicName || leave.employeeName
                      : leave.sr || leave.employeeName}
                  </h4>
                  <p
                    style={{
                      color: isDarkMode
                        ? "rgba(255, 255, 255, 0.6)"
                        : "rgba(26, 26, 26, 0.6)",
                      fontSize: "0.875rem",
                      margin: "0 0 8px 0",
                      fontFamily: "'Cairo', sans-serif",
                    }}
                  >
                    {leave.clientName}
                  </p>
                  <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        padding: "4px 12px",
                        background: `${getLeaveTypeColor(leave.leaveType)}20`,
                        color: getLeaveTypeColor(leave.leaveType),
                        borderRadius: "8px",
                        fontSize: "0.75rem",
                        fontWeight: "600",
                        textTransform: "capitalize",
                        fontFamily: "'Cairo', sans-serif",
                      }}
                    >
                      {React.createElement(LeaveIcon, { size: 14 })}
                      {t.leaveTypes[leave.leaveType] || leave.leaveType}
                    </span>
                  </div>
                </div>

                {/* Dates and Duration */}
                <div
                  style={{ textAlign: language === "ar" ? "right" : "left" }}
                >
                  <div
                    style={{
                      color: isDarkMode ? "white" : "#1a1a1a",
                      fontSize: "0.875rem",
                      fontWeight: "600",
                      marginBottom: "6px",
                      fontFamily: "'Cairo', sans-serif",
                    }}
                  >
                    {new Date(leave.startDate).toLocaleDateString(
                      language === "ar" ? "ar-EG" : "en-US"
                    )}{" "}
                    -{" "}
                    {new Date(leave.endDate).toLocaleDateString(
                      language === "ar" ? "ar-EG" : "en-US"
                    )}
                  </div>
                  <div
                    style={{
                      color: theme.colors.info,
                      fontSize: "1rem",
                      fontWeight: "600",
                      fontFamily: "'Cairo', sans-serif",
                    }}
                  >
                    {leave.days}{" "}
                    {leave.days > 1 ? t.units.days : t.units.day}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default TeamLeaveCalendar;